import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class GenericCommanService {
  private apiUrl = environment.apiUrl.endsWith('/') ? environment.apiUrl : environment.apiUrl + '/';

  constructor(private http: HttpClient) {}

  // Generic overview analysis using simple-search API
  getGenericOverviewAnalysis(payload: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}simple-search/`, payload)
      .pipe(catchError(err => throwError(() => err)));
  }

  // Generic sentiment analysis
  getGenericSentimentAnalysis(payload: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}generic/sentiment-analysis`, payload)
      .pipe(catchError(err => throwError(() => err)));
  }

  // Generic tab
  postGenericTab(tabName: string, payload: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}generic/tab/${tabName}`, payload)
      .pipe(catchError(err => throwError(() => err)));
  }

  // Enhanced tab
  postGenericEnhancedTab(tabName: string, payload: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}generic/enhanced-tab/${tabName}`, payload)
      .pipe(catchError(err => throwError(() => err)));
  }

  // Simplified area comparison
  simplifiedAreaComparison(payload: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}generic/simplified-area-comparison`, payload)
      .pipe(catchError(err => throwError(() => err)));
  }

  // Individual audit endpoints (task-based)
  processEeatAudit(request: { project_id?: string; projectdata_id?: string; filters?: any }): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}generic/audit/eeat-audit`, request)
      .pipe(catchError(err => throwError(() => err)));
  }

  processLandingPageAudit(request: { project_id?: string; projectdata_id?: string; filters?: any }): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}generic/audit/landing-page-audit`, request)
      .pipe(catchError(err => throwError(() => err)));
  }

  processAiAudit(request: { project_id?: string; projectdata_id?: string; filters?: any }): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}generic/audit/ai-audit`, request)
      .pipe(catchError(err => throwError(() => err)));
  }

  // Get audit task status
  getAuditStatus(taskId: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}generic/audit/status/${taskId}`)
      .pipe(catchError(err => throwError(() => err)));
  }

  // Compare simple (brand)
  compareSimple(payload: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}generic/compare-simple`, payload)
      .pipe(catchError(err => throwError(() => err)));
  }

  // Category analysis (generic)
  analyzeCategory(payload: { brands: string[]; category?: string; region?: string }): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}generic/category/analyze`, payload)
      .pipe(catchError(err => throwError(() => err)));
  }

  // AI Citations (generic)
  analyzeAICitations(payload: { brands: string[]; include_competitors: boolean; force_refresh?: boolean }): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}generic/ai-citations/analyze`, payload)
      .pipe(catchError(err => throwError(() => err)));
  }

  // AI Content Index (generic)
  analyzeAIContentIndex(payload: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}generic/ai-content-index`, payload)
      .pipe(catchError(err => throwError(() => err)));
  }

  // Content Creation (generic) - using simple-search API
  analyzeContentCreation(payload: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}simple-search/`, payload)
      .pipe(catchError(err => throwError(() => err)));
  }

  // Simple Search API - Generic method for all analysis types
  simpleSearch(payload: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}simple-search/`, payload)
      .pipe(catchError(err => throwError(() => err)));
  }

  // LLM Comparison Metrics API - Task-based endpoint for ChatGPT/Gemini visibility metrics
  calculateLLMComparisonMetrics(payload: {
    brands: string[];
    category?: string;
    categories?: string[];
    region?: string;
    base_domain?: string;
    language?: string;
    project_id?: string;
    projectdata_id?: string;
  }): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}generic/llm-ai-visibility`, payload)
      .pipe(catchError(err => throwError(() => err)));
  }

  // Get LLM AI visibility task status
  getLLMComparisonMetricsStatus(taskId: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}generic/llm-ai-visibility/${taskId}/status`)
      .pipe(catchError(err => throwError(() => err)));
  }

  // Get LLM AI visibility result (when task is completed). Pass projectdataId to persist result to projectdata.
  getLLMComparisonMetricsResult(taskId: string, projectdataId?: string): Observable<any> {
    const options = projectdataId
      ? { params: { projectdata_id: projectdataId } as Record<string, string> }
      : {};
    return this.http.get<any>(`${this.apiUrl}generic/llm-ai-visibility/${taskId}/result`, options)
      .pipe(catchError(err => throwError(() => err)));
  }

  // Cancel LLM AI visibility task
  cancelLLMComparisonMetricsTask(taskId: string): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}generic/llm-ai-visibility/${taskId}`)
      .pipe(catchError(err => throwError(() => err)));
  }

  // Reload AI Content Index section only (admin). Returns task_id; poll status with getReloadAiContentIndexStatus.
  reloadAiContentIndexSection(projectId: string, projectdataId: string): Observable<{ task_id: string; message?: string }> {
    return this.http.post<{ task_id: string; message?: string }>(`${this.apiUrl}generic/ai-content-index/reload`, {
      project_id: projectId,
      projectdata_id: projectdataId
    }).pipe(catchError(err => throwError(() => err)));
  }

  getReloadAiContentIndexStatus(taskId: string): Observable<{ status: string; error?: string }> {
    return this.http.get<{ status: string; error?: string }>(`${this.apiUrl}generic/ai-content-index/reload/status/${taskId}`)
      .pipe(catchError(err => throwError(() => err)));
  }

  /** Rebuild category tab from AI Visibility + latest competitor discovery data. No bulk/Gemini category tab. */
  rebuildCategoryAnalysisFromVisibility(projectdataId: string): Observable<{ success: boolean; model_meta?: unknown }> {
    return this.http
      .post<{ success: boolean; model_meta?: unknown }>(
        `${this.apiUrl}generic/projectdata/${projectdataId}/rebuild-category-analysis`,
        {}
      )
      .pipe(catchError(err => throwError(() => err)));
  }
}


